import { createContext, useContext, useEffect, useState } from "react";
import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  RecaptchaVerifier,
  signInWithPhoneNumber,
} from "firebase/auth";
import {
  collection,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import { auth, db } from "../firebase";

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading,     setLoading]     = useState(true);

  const fetchUserProfile = async (uid, phone = null) => {
    try {
      let snap;
      const q = query(collection(db, "users"), where("uid", "==", uid));
      snap = await getDocs(q);

      // Phone fallback — OTP login se naya UID milta hai
      if (snap.empty && phone) {
        // Normalize phone — sab formats try karo
        const digits    = phone.replace(/\D/g, "");           // 918755325637
        const with91    = "+" + digits;                        // +918755325637
        const without91 = digits.startsWith("91")
          ? digits.slice(2) : digits;                          // 8755325637

        // Try 1 — +91XXXXXXXXXX (Firebase standard format)
        let phoneSnap = await getDocs(query(
          collection(db, "users"),
          where("phone", "==", with91)
        ));

        // Try 2 — 10 digit without +91
        if (phoneSnap.empty) {
          phoneSnap = await getDocs(query(
            collection(db, "users"),
            where("phone", "==", without91)
          ));
        }

        // Try 3 — raw phone as received
        if (phoneSnap.empty) {
          phoneSnap = await getDocs(query(
            collection(db, "users"),
            where("phone", "==", phone)
          ));
        }

        snap = phoneSnap;

        // UID update karo Firestore mein
        if (!snap.empty) {
          const userDoc = snap.docs[0];
          const { updateDoc } = await import("firebase/firestore");
          await updateDoc(userDoc.ref, { uid });
        }
      }

      if (snap.empty) throw new Error("User profile not found.");
      const userDoc  = snap.docs[0];
      const userData = userDoc.data();
      if (userData.active === false) throw new Error("User account inactive.");
      // D-298: auth uid LAST so a stale userData.uid ("" on the very first
      // OTP login — the snapshot was read before the uid claim) can't
      // overwrite it.
      return { ...userData, uid, docId: userDoc.id };
    } catch (error) {
      console.error("Fetch User Profile Error:", error);
      throw error;
    }
  };

  const login = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(
      auth,
      email.trim().toLowerCase(),
      password.trim()
    );
    // Force token refresh — latest claims mile (syncUserClaims already fired)
    await userCredential.user.getIdToken(true);
    const fullUser = await fetchUserProfile(userCredential.user.uid);
    localStorage.setItem("user", JSON.stringify(fullUser));
    setCurrentUser(fullUser);
    window.dispatchEvent(new Event("userUpdated"));
    return fullUser;
  } catch (error) {
    console.error("Login Error:", error);
    throw error;
  }
};

  const loginWithPhone = async (phoneNumber, containerId = "recaptcha-container") => {
    try {
      // Purana recaptcha completely destroy karo
      if (window._recaptchaVerifier) {
        try { window._recaptchaVerifier.clear(); } catch (_) {}
        window._recaptchaVerifier = null;
      }
      // Container ko reset karo
      const container = document.getElementById(containerId);
      if (container) container.innerHTML = "";

      window._recaptchaVerifier = new RecaptchaVerifier(auth, containerId, {
        size: "invisible",
        callback: () => {},
        "expired-callback": () => { console.warn("Recaptcha expired"); },
      });
      const confirmationResult = await signInWithPhoneNumber(
        auth, phoneNumber, window._recaptchaVerifier
      );
      window._confirmationResult = confirmationResult;
      return confirmationResult;
    } catch (error) {
      console.error("Phone Login Error:", error);
      if (window._recaptchaVerifier) {
        try { window._recaptchaVerifier.clear(); } catch (_) {}
        window._recaptchaVerifier = null;
      }
      throw error;
    }
  };

  const confirmOTP = async (otp) => {
    try {
      if (!window._confirmationResult) {
        throw new Error("OTP session expired. Please request a new OTP.");
      }
      const result = await window._confirmationResult.confirm(otp);
      const uid    = result.user.uid;
      // Force a fresh ID token before querying Firestore — mirrors login()'s
      // email-path behavior. Without this, Firestore's underlying connection
      // can still be using the pre-sign-in token on the very first query,
      // causing a spurious permission-denied that then succeeds on retry —
      // matches the observed "fails first attempt, succeeds second" pattern
      // on brand-new technician logins (12 Aug 2026).
      await result.user.getIdToken(true);
      let fullUser;
      try {
        const phoneNumber = result.user.phoneNumber;
        // Small retry as a defensive backstop, in case the connection still
        // lags the token refresh by a few hundred ms in rare cases.
        let attempts = 0;
        while (true) {
          try {
            fullUser = await fetchUserProfile(uid, phoneNumber);
            break;
          } catch (err) {
            attempts++;
            if (attempts >= 3) throw err;
            await new Promise(res => setTimeout(res, 500));
          }
        }
      } catch (profileErr) {
        await signOut(auth);
        throw new Error(
          "This phone number is not registered in the system. " +
          "Contact your manager to add your number."
        );
      }
      if (fullUser.role !== "technician") {
        await signOut(auth);
        throw new Error("Phone login is only for Technicians. Use email login.");
      }
      localStorage.setItem("user", JSON.stringify(fullUser));
      setCurrentUser(fullUser);
      window.dispatchEvent(new Event("userUpdated")); // ✅
      window._confirmationResult = null;
      if (window._recaptchaVerifier) {
        try { window._recaptchaVerifier.clear(); } catch (_) {}
        window._recaptchaVerifier = null;
      }
      return fullUser;
    } catch (error) {
      console.error("Confirm OTP Error:", error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      localStorage.removeItem("user");
      setCurrentUser(null);
      await signOut(auth);
      window.location.href = "/login";
    } catch (error) {
      console.error("Logout Error:", error);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      try {
        if (firebaseUser) {
          // Phone number bhi pass karo — OTP login ke liye
          const phone = firebaseUser.phoneNumber || null;
          const fullUser = await fetchUserProfile(firebaseUser.uid, phone);
          localStorage.setItem("user", JSON.stringify(fullUser));
          setCurrentUser(fullUser);
          window.dispatchEvent(new Event("userUpdated"));
        } else {
          localStorage.removeItem("user");
          setCurrentUser(null);
        }
      } catch (error) {
        console.error("Auth Restore Error:", error);
        // On network failure, fall back to cached user rather than logging out
        const cached = localStorage.getItem("user");
        if (cached) {
          try {
            setCurrentUser(JSON.parse(cached));
          } catch (_) {
            localStorage.removeItem("user");
            setCurrentUser(null);
          }
        } else {
          setCurrentUser(null);
        }
      } finally {
        setLoading(false);
      }
    });
    return unsubscribe;
  }, []);

  const value = { currentUser, login, loginWithPhone, confirmOTP, logout };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}